import { UnlockOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { ACTIONS_LOCKERS } from 'config/actionsType';
import { useManagePermittedRoles } from 'library/hooks';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCategoriesLockersDashboard } from 'reduxStore/categories/selectors';
import { selectLockersDevices } from 'reduxStore/devices/selectors';
import { devicesActions } from 'reduxStore/devices/slice';
import { Button, Form, Input, Modal, Segmented, Select, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

const SEGMENTED = ['Specific Locker', 'Locker Area'];

const UnlockLockerModal = ({ children, visible: visibleProp, setVisible: setVisibleProp }) => {
	// Hooks
	const {
		token: { fontSizeSM, fontWeightStrong, colorText, marginLG, paddingXXS, paddingXS }
	} = theme.useToken();
	const [form] = Form.useForm();
	const dispatch = useDispatch();
	const confirmLockerAreaValue = Form.useWatch('confirmLockerArea', form)?.trim();
	const assetIDValue = Form.useWatch('assetID', form);
	const categoryIDValue = Form.useWatch('categoryID', form);

	const categories = useSelector(selectCategoriesLockersDashboard);
	const devices = useSelector(selectLockersDevices);
	const { actionDevicesLoading: loading, actionDevicesComplete } = useSelector((state) => state.Devices);

	// Local State
	const [selectedCategory, setSelectedCategory] = useState(null);
	const [activeTab, setActiveTab] = useState(SEGMENTED[0]);
	const [filteredDevices, setFilteredDevices] = useState([]);
	const [internalVisible, setInternalVisible] = useState(false);
	const visible = visibleProp ?? internalVisible;
	const setVisible = setVisibleProp ?? setInternalVisible;

	// ✅ Permissions
	const canRemoteUnlock = useManagePermittedRoles('aCRemoteUnlock');
	const permissionDeniedMessage = "You don't have permission to perform this action.";

	const handleCloseDrawer = useCallback(() => {
		setVisible(false);
		form.resetFields();
		setSelectedCategory(null);
		setFilteredDevices([]);
	}, [form, setVisible]);

	// ⬇️ Effect to handle action completion
	useEffect(() => {
		if (actionDevicesComplete) {
			handleCloseDrawer();
		}
	}, [actionDevicesComplete, handleCloseDrawer]);

	// Handle Segment Change
	const handleTabChange = (key) => {
		setActiveTab(key);
	};

	const handleCategorySelect = (category) => {
		// Filter devices by the selected category
		const filtered = devices.filter((device) => device.categoryID === category.value);

		setFilteredDevices(filtered);
		setSelectedCategory(category.label);

		// Reset the locker number field when category changes
		form.setFieldsValue({ assetID: null, confirmLockerArea: null });
	};

	const handleUnlock = ({ assetID, categoryID }) => {
		if (activeTab === SEGMENTED[0]) {
			// Unlock a specific locker
			dispatch(
				devicesActions.actionDevices({
					deviceIDs: [assetID],
					actionID: ACTIONS_LOCKERS[1]['id']
				})
			);
		} else {
			// Unlock all lockers in the selected category
			dispatch(
				devicesActions.actionDevices({
					categoryIDs: [categoryID],
					actionID: ACTIONS_LOCKERS[1]['id']
				})
			);
		}
	};

	// Prepare options for the Select component
	const devicesOptions = filteredDevices.map((device) => ({
		value: device.deviceID,
		label: device.name
	}));

	// Disable unlock button based on conditions
	const unlockDisabled =
		!categoryIDValue ||
		(activeTab === SEGMENTED[0] && !assetIDValue) ||
		(activeTab === SEGMENTED[1] && confirmLockerAreaValue !== selectedCategory);

	return (
		<Fragment>
			<Tooltip title={!canRemoteUnlock ? permissionDeniedMessage : 'Remote Unlock'} placement='top'>
				<span onClick={(e) => canRemoteUnlock && setVisible(true)}>
					{children ?? (
						<Button
							type='primary'
							icon={<UnlockOutlined />}
							disabled={!canRemoteUnlock || loading}
							loading={loading}
						>
							Remote Unlock
						</Button>
					)}
				</span>
			</Tooltip>
			<Modal
				open={visible}
				onCancel={() => handleCloseDrawer()}
				onClose={() => handleCloseDrawer()}
				centered
				width={425}
				height={416}
				styles={{
					body: {
						padding: `${paddingXXS}px ${paddingXS}px`
					}
				}}
				footer={[
					<Button onClick={() => handleCloseDrawer()} disabled={loading}>
						Cancel
					</Button>,
					<Button
						form={form}
						type='primary'
						key='submit'
						htmlType='submit'
						loading={loading}
						disabled={unlockDisabled}
					>
						Unlock
					</Button>
				]}
			>
				{/* Title Section */}
				<Title level={4} style={{ margin: 0 }}>
					Remote Unlock
				</Title>
				<Text
					style={{
						display: 'block',
						marginBottom: marginLG,
						lineHeight: '20px',
						fontSize: fontSizeSM,
						color: colorText
					}}
				>
					Remotely unlock individual lockers or all lockers in an area to assist users experiencing issues.
				</Text>

				{/* Tabs for Specific Locker / Locker Area */}
				<Segmented value={activeTab} onChange={handleTabChange} options={SEGMENTED} block />

				{/* Form Section */}
				<Form
					id={form}
					form={form}
					layout='vertical'
					style={{ marginTop: 16 }}
					onFinish={(values) => handleUnlock(values)}
				>
					<Form.Item
						label='Locker Area'
						name='categoryID'
						required={false}
						rules={[{ required: true, message: 'Please select the Locker Area' }]}
						style={{
							fontWeight: fontWeightStrong
						}}
					>
						<Select
							style={{
								fontWeight: 400
							}}
							placeholder='Select Locker Category'
							options={categories.map((category) => ({
								value: category.categoryID,
								label: category.name
							}))}
							onChange={(e, value) => handleCategorySelect(value)}
						/>
					</Form.Item>

					{activeTab === SEGMENTED[0] ? (
						<Form.Item
							label='Locker Number'
							name='assetID'
							required={false}
							rules={[{ required: true, message: 'Please select the Locker Number' }]}
							style={{
								fontWeight: fontWeightStrong
							}}
						>
							<Select
								placeholder='Select Locker Number'
								options={devicesOptions}
								style={{
									fontWeight: 400
								}}
								showSearch
								filterOption={(input, option) => {
									return option.label.toLowerCase().includes(input.toLowerCase());
								}}
							/>
						</Form.Item>
					) : (
						<Form.Item
							label={
								<Text strong style={{ fontWeight: fontWeightStrong }}>
									Type{' '}
									<span style={{ fontWeight: 700 }}>{selectedCategory ? selectedCategory : ''}</span>{' '}
									to confirm
								</Text>
							}
							name='confirmLockerArea'
							required={false}
							rules={[
								{
									required: true,
									message: 'Please type the Locker Area name'
								},
								() => ({
									validator(_, value) {
										if (!value || value.trim() === selectedCategory) {
											return Promise.resolve();
										}
										return Promise.reject(new Error(`Must match: ${selectedCategory}`));
									}
								})
							]}
						>
							<Input style={{ fontWeight: 400 }} placeholder='Type the Locker Area name' />
						</Form.Item>
					)}
				</Form>
			</Modal>
		</Fragment>
	);
};

export default UnlockLockerModal;
